import React, { forwardRef, useRef, useMemo } from "react";
import { CpButton, CpDropdown, CpLabel } from "@components";
import { CpCalendar } from "../cp-calendar/cp-calendar";
import { CpDatePickerInput } from "./date-input/cp-date-picker-input.component";
import styles from "./cp-date-picker.module.css";
import {
  createDateFromStr,
  ValidationError,
} from "./date-input/date-validation.helpers";
import { CalendarProps } from "react-calendar";
import { always, maybe } from "src/utils";

export type CpDatePickerProps = {
  /**
   * Automatically close the calendar when a date is selected
   * @default true
   */
  autoCloseOnSelect?: boolean;
  /**
   * Fill the parent container width
   * @default false
   */
  block?: boolean;
  /**
   * Class name to apply to the date picker
   */
  className?: string;
  /**
   * Disable user interaction with the date picker
   */
  disabled?: boolean;
  /**
   * Hide the calendar button
   * @default false
   */
  disableCalendar?: boolean;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * ID for the date picker
   */
  id?: string;
  /**
   * Label for the date picker
   */
  label?: string;
  /**
   * Maximum date that can be selected (YYYY-MM-DD format)
   */
  maxDate?: string;
  /**
   * Minimum date that can be selected (YYYY-MM-DD format)
   */
  minDate?: string;
  /**
   * Name attribute for form submission
   */
  name?: string;
  /**
   * Called when the date is changed to a valid or empty date
   */
  onChange?: (isoString: string) => void;
  /**
   * Called when validation state changes, provides an array of validation errors `["required", "invalid-date", "before-min", "after-max"]`
   */
  onValidityChange?: (validationErrors: ValidationError[]) => void;
  /**
   * Mark the date picker as required for form validation
   * @default false
   */
  required?: boolean;
  /**
   * Date string in YYYY-MM-DD format
   */
  value?: string;
};

export const CpDatePicker = forwardRef<HTMLInputElement, CpDatePickerProps>(
  function CpDatePicker(props, ref) {
    const {
      autoCloseOnSelect = true,
      block,
      className,
      disabled,
      disableCalendar = false,
      error,
      id,
      label,
      maxDate,
      minDate,
      name,
      onChange,
      onValidityChange,
      required,
      value,
      ...restProps
    } = props;

    const closeDropdownRef = useRef<(() => void) | null>(null);
    const containerClasses = always(
      styles.container,
      className,
      maybe(error, styles.error),
      maybe(disabled, styles.disabled),
      maybe(block, styles.block),
    );

    const dateValue = useMemo(() => {
      return createDateFromStr(value);
    }, [value]);

    const minDateObject = useMemo(() => {
      return createDateFromStr(minDate);
    }, [minDate]);

    const maxDateObject = useMemo(() => {
      return createDateFromStr(maxDate);
    }, [maxDate]);

    const handleDatePickerChange = (value: string) => {
      onChange?.(value);
    };

    const handleCalendarChange: CalendarProps["onChange"] = (value) => {
      // Handle react-calendar onChange which can return Date, Date[], or null
      const selectedDate = Array.isArray(value) ? value[0] : value;
      if (!selectedDate) {
        onChange?.("");
        return;
      }

      // Convert date to YYYY-MM-DD format
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const newValue = `${year}-${month}-${day}`;

      onValidityChange?.([]);
      onChange?.(newValue);

      if (autoCloseOnSelect) {
        closeDropdownRef.current?.();
      }
    };

    return (
      <div {...restProps} className={containerClasses}>
        {label && (
          <CpLabel htmlFor={id} error={!!error} required={required}>
            {label}
          </CpLabel>
        )}
        <div className={styles.fieldContainer}>
          <div className={styles.inputWrapper}>
            <CpDatePickerInput
              ref={ref}
              name={name}
              disabled={disabled}
              required={required}
              minDate={minDate}
              maxDate={maxDate}
              value={value || ""}
              onChange={handleDatePickerChange}
              onValidityChange={onValidityChange}
              className={styles.dateInput}
            />
            {!disableCalendar && (
              <CpDropdown
                position="bottom-start"
                contentWidth={260}
                renderTrigger={({ toggle }) => (
                  <CpButton
                    onClick={disabled ? undefined : toggle}
                    type="button"
                    className={styles.calendarButton}
                    disabled={disabled}
                    icon="misc-calendar"
                    small
                    aria-label="Open calendar"
                    tabIndex={disabled ? -1 : 0}
                  />
                )}
                renderContent={({ close }) => {
                  closeDropdownRef.current = close;
                  return (
                    <CpCalendar
                      value={dateValue}
                      onChange={handleCalendarChange}
                      minDate={minDateObject || undefined}
                      maxDate={maxDateObject || undefined}
                    />
                  );
                }}
                allowContentClicks
              />
            )}
          </div>
        </div>
        {error && <div className={styles.message}>{error}</div>}
      </div>
    );
  },
);
